import { inject, Injectable } from '@angular/core';
import { PaymentDetailBottomSheetComponent } from '../components/payment-detail-bottom-sheet/payment-detail-bottom-sheet.component';
import { PurchaseService } from './purchase-service.service';
import { BehaviorSubject } from 'rxjs';
import { Location } from '@angular/common';
import { IPayment } from '../../../components/core/models/payment.interface';
import { ErrorService } from '../../../components/core/services/error.service';
import { FundDataService } from '../../../components/core/services/fund-data.service';
import { MaknaAuthenticationService } from '../../makna-authentication/services/makna-authentication.service';
import {
  CHOICE_PAYMENT_METHOD_ROUTE,
  CREATE_PASSWORD_ROUTE,
  INVESTMENT_LIST_ROUTE,
  NATIONAL_ID_ROUTE,
  OTP_ROUTE,
  RESULT_ROUTE,
  SIGN_AGREEMENTS_ROUTE,
} from '../../../data-access/constants/app-routes';
import { EVerifyCustomerState } from '../../../components/core/models/verify-customer-state.enum';
import { ICreateFundPayment } from '../../../components/core/models/create-fund-payment.interface';
import { environment } from '../../../data-access/environments/environment';
import { EIntrackEventName } from '../../../components/core/models/intrack-event-name.enum';
import { ErrorCodes } from '../../../data-access/enums/error-codes';
import { WealthNavigationService } from '@client-monorepo/wealth/navigation';
import { ICrowdFundingPurchaseData } from '../../crowds/models/crowd-funding-purchase-data';
import { EIpoPaymentMethodAgreement } from '../../choice-payment-way/models/ipo-payment-method-agreement';
import { NgxBottomSheetService } from '@digipay/ngx-bottom-sheet';
import { NgxEventTrackerService } from '@digipay/ngx-event-tracker';

@Injectable({
  providedIn: 'root',
})
export class PaymentHandlerService {
  private static _PAYMENT: IPayment | ICrowdFundingPurchaseData;
  navigationService = inject(WealthNavigationService);

  private loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  loading$ = this.loading.asObservable();

  private bottomSheet = inject(NgxBottomSheetService);
  private eventService = inject(NgxEventTrackerService);

  private location = inject(Location);
  private errorService = inject(ErrorService);
  private purchaseService = inject(PurchaseService);
  private fundDataService = inject(FundDataService);
  private maknaAuthenticationService = inject(MaknaAuthenticationService);

  public setPayment(payment: IPayment | ICrowdFundingPurchaseData) {
    PaymentHandlerService._PAYMENT = payment;
  }

  private get getPayment(): any {
    return PaymentHandlerService._PAYMENT;
  }

  public async paymentConfirm(): Promise<boolean> {
    return new Promise(() => {
      this.maknaAuthenticationService.isUserCompletelyRegistered().subscribe((registerRes) => {
        if (registerRes.success && registerRes.result) {
          if (this.getPayment) {
            const isCrowdFunding = this.getPayment?.assetData?.investmentType == 'CrowdFund';
            const baseAmount = this.getPayment.amount;
            this.getPayment.amount = isCrowdFunding ? this.roundedAmount(this.getPayment.amount) : this.getPayment.amount;
            this.createPayment(baseAmount, isCrowdFunding);
          } else {
            this.navigationService.navigate([INVESTMENT_LIST_ROUTE]);
          }
        } else {
          this.navigationService.navigate([CREATE_PASSWORD_ROUTE], {
            state: {
              symbol: this.getPayment.symbol,
              amount: this.getPayment.amount,
              units: this.getPayment.units,
              type: this.getPayment.type,
              agreementChecked: this.getPayment.agreementChecked,
              assetData: this.getPayment.assetData,
            },
          });
        }
      });
    });
  }

  private createPayment(baseAmount: number, isCrowdFunding: boolean) {
    this.fundDataService.createFundPayment(this.getPayment, isCrowdFunding).subscribe((resFundResult) => {
      if (resFundResult.success) {
        resFundResult.result['amountRounded'] = baseAmount !== this.getPayment.amount;
        resFundResult.result['ipoPaymentMethod'] = this.getPayment.ipoPaymentMethod;
        resFundResult.result['isIPO'] = this.getPayment.type === 'IPO';

        this.bottomSheet.openBottomSheet(
          PaymentDetailBottomSheetComponent,
          { data: resFundResult.result },
          {
            noPadding: true,
          },
        );

        this.bottomSheet.onClose.subscribe(() => {
          const res = this.bottomSheet.outputData();
          if (res && res.aggreement) {
            this.onBuy(res.paymentData());
          }
          this.loading.next(false);
        });
      }
    });
  }

  public handleState(state: EVerifyCustomerState, routerState: any): Promise<boolean> {
    return new Promise((resolve) => {
      switch (state) {
        case EVerifyCustomerState.RequiresNationalId:
          this.navigationService.navigate([NATIONAL_ID_ROUTE], {
            state: routerState,
          });
          break;
        case EVerifyCustomerState.RequiresOtp:
          this.navigationService.navigate([OTP_ROUTE], {
            state: routerState,
          });
          break;
        case EVerifyCustomerState.RequiresAgreement:
          this.navigationService.navigate([SIGN_AGREEMENTS_ROUTE], {
            state: routerState,
          });
          break;
        case EVerifyCustomerState.Verified:
          if (routerState.type === 'IPO') {
            this.navigationService.navigate([CHOICE_PAYMENT_METHOD_ROUTE, routerState.symbol], {
              state: routerState,
            });
          } else {
            this.paymentConfirm();
          }
          break;
      }
      resolve(true);
    });
  }

  private onBuy(data: ICreateFundPayment): Promise<boolean> {
    const isCrowdFunding = this.getPayment?.assetData?.investmentType == 'CrowdFund';
    return new Promise((resolve) => {
      this.purchaseService
        .createPayment(
          data.orderId,
          environment.callbackUrl,
          isCrowdFunding ? environment.clientMetadata + '?type=CrowdFund' : environment.clientMetadata,
          isCrowdFunding,
          this.getPayment.ipoPaymentMethod,
        )
        .subscribe((res) => {
          if (res?.success) {
            this.eventService.sendEvent({
              eventName: EIntrackEventName.PAYMENT_START,
              eventData: {
                Fund_Id: this.getPayment.symbol,
                Amount: this.getPayment.amount,
              },
            });
            const response = isCrowdFunding ? null : JSON.parse(res.result.params);
            this.location.replaceState(this.navigationService.getCurrentUrl(), `expired=true&symbol=${data.instrumentSymbol}`);
            if (res.result.method === 'IPG_POST') {
              this.errorService.setErrorPageExpired(true);
              this.openIPGUsingPostMethod(res.result.url, response);
            } else if (res.result.method === 'IPG_GET') {
              let queryParams = '';
              for (const key in response) {
                if (Object.prototype.hasOwnProperty.call(response, key)) {
                  const param = response[key];
                  queryParams = `${key}=${queryParams.concat(param)}&`;
                }
              }
              queryParams = queryParams.slice(0, queryParams.length - 1);
              const URL = queryParams ? res.result.url + '?' + queryParams : res.result.url;
              this.errorService.setErrorPageExpired(true);
              window.open(URL, '_self');
            } else if (this.getPayment.ipoPaymentMethod === EIpoPaymentMethodAgreement.ByCredit) {
              this.navigationService.navigate([RESULT_ROUTE], {
                queryParams: {
                  status: 'Succeed',
                  action: 'buy',
                  symbol: data.instrumentSymbol,
                  receiptNumber: res.result.remoteOrderId,
                  broker: res.result.method === 'BROKER_CREDIT',
                  ipoDate: res.result?.metadata?.IpoDate?.replaceAll('/', '-'),
                },
              });
            } else {
              this.navigationService.navigate([RESULT_ROUTE], {
                queryParams: {
                  status: 'Succeed',
                  action: 'buy',
                  symbol: data.instrumentSymbol,
                  receiptNumber: res.result.remoteOrderId,
                },
              });
            }
          } else {
            if (res?.error?.code === ErrorCodes.orderStatusIsNotDraft) {
              this.navigationService.navigate([RESULT_ROUTE], {
                queryParams: {
                  symbol: data.instrumentId,
                },
              });
            }
          }
          this.loading.next(false);
          resolve(true);
        });
    });
  }

  private openIPGUsingPostMethod(url: string, data: any): void {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = url;
    form.target = '_self';
    if (data) {
      Object.keys(data).forEach((key) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = data[key];
        form.appendChild(input);
      });
    }
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  }

  private roundedAmount(amount: number): number {
    return Math.floor(amount / 1000) * 1000;
  }
}
